import type { TemplateFormField } from '@common/types/templateFormFields';
import { isValidEmail } from '@common/utils/isValidEmail';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography, useMediaQuery } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FormComponent from '@/components/FormComponent/FormComponent';
import LawFirmLogo from '@/components/LawFirmLogo';
import { ordinalNumbers } from '@/constants';
import ClientViewWrapper from '@/pages/QuoteIntakeClientView/ClientViewWrapper';
import { clientSideFormSubmit } from '@/services/ClientSideTemplateFormService';
import useQuoteFormClientViewStore from '@/stores/useQuoteFormClientViewStore';
import { convertToHtml, validate } from '@/utils';
import { isRetryableClientLoadError } from '@/utils/queryError';
import { errorSnackbar, warningSnackbar } from '@/utils/snackbar';
import ButtonSection from './ButtonSection';
import ClientErrorPage from './ClientErrorPage';
import FormContainer from './FormContainer';
import { mainWrapperStyles } from './styles';

const getLowestGroupsLowestEl = () => {
    const formGroupsCount = 10;
    let lowest: any = null;

    for (let i = 0; i <= formGroupsCount; i++) {
        const formGroupItems = document.querySelectorAll(`[data-formgroup-index="${i}"]`);
        for (let item = 0; item < formGroupItems.length; item++) {
            let order: any = formGroupItems[item].getAttribute('data-is-error');
            order = parseInt(order, 10);

            if (lowest === null || order < parseInt(lowest?.getAttribute('data-is-error'), 10)) {
                lowest = formGroupItems[item];
            }
        }
    }

    return lowest;
};

const getLowestEl = () => {
    const els = document.querySelectorAll('[data-is-error]');
    let lowest: any = els.length ? els[0] : null;
    for (let i = 0; i < els.length; i++) {
        let order: any = els[i].getAttribute('data-is-error');
        order = parseInt(order, 10);
        lowest = lowest.getAttribute('data-is-error') > order ? els[i] : lowest;
    }
    // Todo: Handle the scrolling logic inside the main component itself
    return lowest;
};
const initScroll = (el) => {
    if (el) {
        window.setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        }, 1000);
    }
};

export default function QuoteFormView() {
    const theme = useTheme();
    const [clientViewData, updateTemplateFormField, updateVisibleToUser, error, loading, setIsSubmitted, fetchQuoteFormData, isQuoteFormPresent, setError, setLoading, quoteProcess] =
        useQuoteFormClientViewStore((state) => [
            state.formData,
            state.updateTemplateFormField,
            state.updateVisibleToUser,
            state.error,
            state.loading,
            state.setIsSubmitted,
            state.fetchQuoteFormData,
            state.isQuoteFormPresent,
            state.setError,
            state.setLoading,
            state.quoteProcess,
        ]);
    const { quoteTemplateId, lawFirmId } = useParams();
    const [expand, setExpand] = useState<string | false>('0');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let subtitle: string;

    if (quoteProcess === 'Purchase') {
        subtitle = 'Purchaser';
    } else if (quoteProcess === 'Refinance') {
        subtitle = 'Borrower';
    } else if (quoteProcess === 'Sale') {
        subtitle = 'Seller';
    } else if (quoteProcess === 'PrivateLender') {
        subtitle = 'Lender';
    }

    const formDescriptionText = clientViewData.quoteForm.isSubmitted ? clientViewData.quoteForm.footerTemplate : clientViewData.quoteForm.headerDescription;

    // this is act as a flag for showing the email Duplication Error
    let emailDuplicateError = false;

    // this is act as a flag for showing the valid error message of email based on the user input
    let showInValidEmailMessage = false;

    // Track if submit has been attempted to control error display
    const [hasSubmitted, setHasSubmitted] = useState(false);
    // Whether the current sticky error is worth a Retry (false for terminal load failures like 404)
    const [canRetryError, setCanRetryError] = useState(true);
    // track the number of purchasers
    const [numPurchasers, setNumPurchasers] = useState(0);
    // Create initial purchasers according to numPurchasers
    const createInitialPurchasers = (count: number) => {
        return Array.from({ length: count }).map(() => ({
            firstName: {
                formFieldId: '',
                formFieldResponse: '',
                helper: 'This field is required',
                isError: true,
            },
            lastName: {
                formFieldId: '',
                formFieldResponse: '',
                helper: 'This field is required',
                isError: true,
            },
            email: {
                formFieldId: '',
                formFieldResponse: '',
                helper: 'This field is required',
                isError: true,
            },
            primaryPhone: {
                formFieldId: '',
                formFieldResponse: '',
                helper: 'This field is required',
                isError: true,
            },
            middleName: {
                formFieldId: '',
                formFieldResponse: '',
                helper: '',
                isError: false,
            },
        }));
    };
    // State to hold purchaser fields
    const [purchaserFields, setPurchaserFields] = useState<any[]>(createInitialPurchasers(numPurchasers));
    // according to number of purchasers we create initial purchasers and also keep the previous values
    useEffect(() => {
        setPurchaserFields((prev) => {
            if (numPurchasers > prev.length) {
                const value = numPurchasers - prev.length;
                return [...prev, ...createInitialPurchasers(value)];
            } else if (prev.length > numPurchasers) {
                return prev.slice(0, numPurchasers);
            }
            return prev; // when numPurchasers === prev.length
        });
    }, [numPurchasers]);

    const handleValueChange = (name, value, field) => {
        // check the show/hide logic for conditional questions
        if (field.questionSchema.conditions?.length) {
            const fieldsArr = [...clientViewData.quoteForm.templateFormFields];
            field.questionSchema.conditions.forEach((condition) => {
                for (let i = 0; i < fieldsArr.length; i++) {
                    const question = { ...fieldsArr[i] };
                    if (question.questionSchema.name === condition?.nextQuestion?.name) {
                        if (value === condition?.value) {
                            updateVisibleToUser(question.questionSchema.name, true);
                        } else {
                            updateVisibleToUser(question.questionSchema.name, false);
                        }
                        break;
                    }
                }
            });
        }

        // Onchange Validations
        if (field) {
            let isValid = true;
            const isRequired = field.questionValidationSchema?.required;
            const isRequiredAndNotEmpty = isRequired && value;
            const onChangeChecks = field.questionValidationSchema?.onChange || field.questionValidationSchema?.onchange;

            // Date validation (similar to handleSubmit)
            if (field.questionSchema.type === 'date') {
                const isRequired = !!field.questionValidationSchema?.required;
                const isRequiredAndEmpty = isRequired && (value === '' || value === null || value === undefined);

                const updatedFormData = produce(clientViewData, (draftState) => {
                    const fieldIndex = draftState.quoteForm.templateFormFields.findIndex((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                    if (fieldIndex === -1) return;
                    const draftField = draftState.quoteForm.templateFormFields[fieldIndex];
                    const selectedDate = DateTime.fromJSDate(new Date(value));

                    // Only show errors if submit has been attempted
                    if (hasSubmitted) {
                        // Required validation for date
                        if (isRequired) {
                            draftField.questionSchema.isError = !!isRequiredAndEmpty;
                            draftField.questionSchema.helper = isRequiredAndEmpty ? 'This field is required' : '';
                        }

                        // Date format and range validation
                        if (value) {
                            if (!selectedDate?.isValid) {
                                draftField.questionSchema.isError = true;
                                draftField.questionSchema.helper = 'Invalid Date';
                            } else if (selectedDate.year < 1900) {
                                draftField.questionSchema.isError = true;
                                draftField.questionSchema.helper = 'Invalid Date';
                            } else if (selectedDate.year > 2099) {
                                draftField.questionSchema.isError = true;
                                draftField.questionSchema.helper = 'Invalid Date';
                            } else {
                                draftField.questionSchema.isError = false;
                                draftField.questionSchema.helper = '';
                            }
                        }

                        // If not required and empty, clear error
                        if (!value && !isRequired) {
                            draftField.questionSchema.isError = false;
                            draftField.questionSchema.helper = '';
                        }
                        // If not visible, clear error
                        if (!draftField.isVisibleToUser) {
                            draftField.questionSchema.isError = false;
                            draftField.questionSchema.helper = '';
                        }
                    }
                    draftField.questionSchema.value = value;
                });

                const updatedField = updatedFormData.quoteForm.templateFormFields.find((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                if (updatedField) {
                    updateTemplateFormField(updatedField, field.questionSchema.name);
                }
                return; // skip the rest for date fields
            }

            if (onChangeChecks) {
                Object.keys(onChangeChecks).forEach((check) => {
                    const type = check === 'type' ? onChangeChecks[check] : check;
                    const extraParam = check === 'type' ? null : onChangeChecks[check];
                    const validationStatus = validate(type, value, extraParam);
                    if (!validationStatus || typeof validationStatus !== 'boolean') {
                        isValid = false;
                    }

                    // set the updated data on store
                    // use produce to set the data on the field in the store
                    const updatedFormData = produce(clientViewData, (draftState) => {
                        const fieldIndex = draftState.quoteForm.templateFormFields.findIndex((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.isError = !isValid;
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.helper = validationStatus ? validationStatus : null;
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.value = value;
                        if (value === '') {
                            draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.isError = false;
                            draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.helper = undefined;
                        }
                    });

                    const updatedField = updatedFormData.quoteForm.templateFormFields.find((formField) => formField.templateFormFieldId === field.templateFormFieldId);

                    if (updatedField) {
                        updateTemplateFormField(updatedField, field.questionSchema.name);
                    }
                });
            } else {
                const updatedFormData = produce(clientViewData, (draftState) => {
                    const fieldIndex = draftState.quoteForm.templateFormFields.findIndex((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                    draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.value = value;
                    // if isRequired validation is present
                    // only then check for the isRequiredAndNotEmpty Case
                    if (isRequired) {
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.isError = !isRequiredAndNotEmpty;
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.helper = isRequiredAndNotEmpty ? '' : 'This field is required';
                    }
                });

                const updatedField = updatedFormData.quoteForm.templateFormFields.find((formField) => formField.templateFormFieldId === field.templateFormFieldId);

                if (updatedField) {
                    updateTemplateFormField(updatedField, field.questionSchema.name);
                }
            }
        }
        // handle the number of purchasers
        if (name === 'purchasersListedOnPurchaseAndSaleAgreement') {
            setNumPurchasers(Number(value) || 0);
            return;
        }
    };

    // handle purchaser field changes
    const handlePurchaserFieldChange = (fieldKey: string, value: string, templateField: TemplateFormField, purchaserIndex: number) => {
        setPurchaserFields((prev) => {
            const updated = [...prev];
            if (!updated[purchaserIndex]) updated[purchaserIndex] = {};

            updated[purchaserIndex][fieldKey] = {
                formFieldId: templateField.templateFormFieldId,
                formFieldResponse: value,
            };
            return updated;
        });
    };
    const handleSubmit = async () => {
        const fieldValueMap: Record<any, any> = {};
        purchaserFields.forEach((purchaser, purchaserIndex) => {
            Object.keys(purchaser).forEach((fieldName) => {
                const field = purchaser[fieldName];
                if (!fieldValueMap[field.formFieldId]) {
                    // initialize with the right number of purchasers filled with ''
                    fieldValueMap[field.formFieldId] = Array(purchaserFields.length).fill('');
                }
                // Always assign purchaser’s value (or empty string if missing)
                fieldValueMap[field.formFieldId][purchaserIndex] = field.formFieldResponse || '';
            });
        });
        // Now build the final output
        const userFields = Object.entries(fieldValueMap)
            .map(([formFieldId, values]) => ({
                formFieldId,
                formFieldResponse: Array.from(values).join('|'),
            }))
            .filter((el) => el.formFieldId !== '');
        setHasSubmitted(true);
        try {
            //Check if the form exists before submitting
            await isQuoteFormPresent(quoteTemplateId);
        } catch (error) {
            console.error(error);
            return;
        }

        const formData = clientViewData.quoteForm.templateFormFields;

        const formDataWithoutDetails = formData.filter((el) => el.name !== 'firstName' && el.name !== 'middleName' && el.name !== 'lastName' && el.name !== 'email' && el.name !== 'primaryPhone');
        let isValid = true;
        let noOnChangeError = true;
        formDataWithoutDetails.forEach((field) => {
            const isRequired = field.questionValidationSchema?.required;
            const isRequiredAndEmpty = isRequired && (field.questionSchema.value === '' || field.questionSchema.value === null || field.questionSchema.value === undefined);

            const isVisibleToUser = field.isVisibleToUser;
            // check for required fields
            if (isRequiredAndEmpty && isVisibleToUser) {
                isValid = false;
            }

            // Date Validation (similar to required validation block)
            if (field.questionSchema.type === 'date') {
                const isRequired = !!field.questionValidationSchema?.required;
                const isRequiredAndEmpty = isRequired && (field.questionSchema.value === '' || field.questionSchema.value === null || field.questionSchema.value === undefined);

                // Use produce to update the date field validation
                const updatedFormData = produce(clientViewData, (draftState) => {
                    const fieldIndex = draftState.quoteForm.templateFormFields.findIndex((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                    if (fieldIndex === -1) return;
                    const draftField = draftState.quoteForm.templateFormFields[fieldIndex];
                    const selectedDate = DateTime.fromJSDate(new Date(draftField.questionSchema.value));

                    // Required validation for date
                    if (isRequired) {
                        draftField.questionSchema.isError = !!isRequiredAndEmpty;
                        draftField.questionSchema.helper = isRequiredAndEmpty ? 'This field is required' : '';
                    }

                    // Date format and range validation
                    if (draftField.questionSchema.value) {
                        if (!selectedDate?.isValid) {
                            draftField.questionSchema.isError = true;
                            draftField.questionSchema.helper = 'Invalid Date';
                            noOnChangeError = false;
                        } else if (selectedDate.year < 1900) {
                            draftField.questionSchema.isError = true;
                            draftField.questionSchema.helper = 'Invalid Date';
                            noOnChangeError = false;
                        } else if (selectedDate.year > 2099) {
                            draftField.questionSchema.isError = true;
                            draftField.questionSchema.helper = 'Invalid Date';
                            noOnChangeError = false;
                        } else {
                            draftField.questionSchema.isError = false;
                            draftField.questionSchema.helper = '';
                            noOnChangeError = true;
                        }
                    }

                    // If not required and empty, clear error
                    if (!draftField.questionSchema.value && !isRequired) {
                        draftField.questionSchema.isError = false;
                        draftField.questionSchema.helper = '';
                        noOnChangeError = true;
                    }
                    // If not visible, clear error
                    if (!draftField.isVisibleToUser) {
                        draftField.questionSchema.isError = false;
                        draftField.questionSchema.helper = '';
                        noOnChangeError = true;
                    }
                });

                // Get the field that has been updated
                const updatedField = updatedFormData.quoteForm.templateFormFields.find((formField) => formField.templateFormFieldId === field.templateFormFieldId);

                // Update the zustand state using the setters
                if (updatedField) {
                    updateTemplateFormField(updatedField, field.questionSchema.name);
                }
            }

            // check for onChange validations
            // ignore the hidden questions
            if (field.questionSchema.isError && field.isVisibleToUser) {
                noOnChangeError = false;
            }

            // Set the error on required fields
            if (isRequiredAndEmpty) {
                // Use produce method from the zustand library for updating the state
                const updatedFormData = produce(clientViewData, (draftState) => {
                    const fieldIndex = draftState.quoteForm.templateFormFields.findIndex((formField) => formField.templateFormFieldId === field.templateFormFieldId);
                    // if isRequired validation is present
                    // only then check for the isRequiredAndNotEmpty Case
                    if (isRequired) {
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.isError = !!isRequiredAndEmpty;
                        draftState.quoteForm.templateFormFields[fieldIndex].questionSchema.helper = isRequiredAndEmpty ? 'This field is required' : '';
                    }
                });

                // Get the field that has been updated
                const updatedField = updatedFormData.quoteForm.templateFormFields.find((formField) => formField.templateFormFieldId === field.templateFormFieldId);

                // Update the zustand state using the setters
                updateTemplateFormField(updatedField as any, field.questionSchema.name);
            }
        });

        const requiredFields = ['firstName', 'lastName', 'email', 'primaryPhone'];

        const updatedPurchaserFields = purchaserFields.map((purchaser) => ({
            ...purchaser,
        }));
        updatedPurchaserFields.forEach((purchaser, index) => {
            const isAllFilled = requiredFields.every((fieldKey) => {
                const fieldValue = purchaser[fieldKey];
                return fieldValue?.formFieldId && fieldValue?.formFieldResponse && fieldValue.formFieldResponse !== '';
            });
            if (!isAllFilled) {
                isValid = false;
            }
            Object.entries(purchaser).forEach(([fieldKey, fieldValue]) => {
                if (fieldKey === 'middleName') return;
                const hasValue = fieldValue?.formFieldId && fieldValue?.formFieldResponse && fieldValue.formFieldResponse !== '';

                // validation of email

                if (fieldKey === 'email') {
                    if (!hasValue) {
                        showInValidEmailMessage = false;
                        isValid = false;
                        updatedPurchaserFields[index][fieldKey] = {
                            ...fieldValue,
                            isError: true,
                            helper: 'This field is required',
                        };
                    } else if (!isValidEmail(fieldValue.formFieldResponse)) {
                        showInValidEmailMessage = true;
                        isValid = false;
                        updatedPurchaserFields[index][fieldKey] = {
                            ...fieldValue,
                            isError: true,
                            helper: 'This must be a valid email',
                        };
                    } else {
                        showInValidEmailMessage = false;
                        updatedPurchaserFields[index][fieldKey] = {
                            ...fieldValue,
                            isError: false,
                            helper: '',
                        };
                    }
                    return;
                }

                if (hasValue) {
                    updatedPurchaserFields[index][fieldKey] = {
                        ...fieldValue,
                        isError: false,
                        helper: '',
                    };
                } else {
                    updatedPurchaserFields[index][fieldKey] = {
                        ...fieldValue,
                        isError: true,
                        helper: 'This field is required',
                    };
                }
            });
            // It checks for duplicate emails
            const emails = updatedPurchaserFields.map((p) => p.email?.formFieldResponse?.trim().toLowerCase()).filter(Boolean);
            const emailSet = new Set();
            emails.forEach((email, idx) => {
                if (emailSet.has(email)) {
                    emailDuplicateError = true;
                    updatedPurchaserFields[idx].email = {
                        ...updatedPurchaserFields[idx].email,
                        isError: true,
                        helper: 'Duplicate email. Must be unique.',
                    };
                    isValid = false;
                } else {
                    emailSet.add(email);
                }
            });
        });
        setPurchaserFields(updatedPurchaserFields);
        if (!isValid) {
            // Add the scrollIntoView functionality for first field with error
            window.setTimeout(() => {
                const el = getLowestGroupsLowestEl();

                console.log(el);
                const groupIndex = el?.getAttribute('data-formgroup-index');

                console.log(groupIndex);

                if (groupIndex != null) {
                    setExpand(`${groupIndex}`);
                }
                // Scroll to the component
                initScroll(el);

                initScroll(getLowestEl());
            }, 500);
            if (emailDuplicateError) {
                warningSnackbar('Email address must be unique.', true);
            } else if (showInValidEmailMessage) {
                warningSnackbar('Enter a valid email address.', true);
            } else {
                warningSnackbar('Please fill all required fields.', true);
            }
            return;
        }

        if (!noOnChangeError) {
            warningSnackbar('Please correct the errors.', true);
            return;
        }

        const fieldsToAdd = ['firstName', 'middleName', 'lastName', 'email', 'primaryPhone'];

        const payload: any = {};

        payload.contacts = purchaserFields.map((person, index) => {
            const contact: any = {};

            fieldsToAdd.forEach((field) => {
                const value = person?.[field]?.formFieldResponse;
                if (field === 'email') {
                    contact.contactEmail = value;
                } else if (field === 'primaryPhone') {
                    contact.phoneNumber = value;
                } else {
                    contact[field] = value;
                }
            });

            contact.isPrimary = index === 0;
            return contact;
        });
        payload.submittedBy = formData.find((field) => field.questionSchema.name === 'email')?.questionSchema.value?.toString() || null;
        payload.templateFormId = clientViewData.quoteForm.templateFormId || '';
        const templateFormFields = formData
            .filter(
                (value) =>
                    value.name !== 'firstName' &&
                    value.name !== 'middleName' &&
                    value.name !== 'lastName' &&
                    value.name !== 'email' &&
                    value.name !== 'primaryPhone' &&
                    (value.isVisibleToUser || ['purchasePrice', 'salePrice', 'totalLoanAmount'].includes(value.questionSchema.name)),
            )
            .map((value) => {
                if (['purchasePrice', 'salePrice', 'totalLoanAmount'].includes(value.questionSchema.name)) {
                    return {
                        formFieldId: value.templateFormFieldId || '',
                        formFieldResponse: value.questionSchema.value !== '' ? value.questionSchema.value?.toString() : '0',
                    };
                }
                if (value.questionSchema.type === 'upload' && value?.questionSchema?.value) {
                    const fileIds = value.questionSchema.value;
                    return {
                        formFieldId: value.templateFormFieldId || '',
                        formFieldResponse: fileIds,
                    };
                } else if (value.questionSchema.type === 'date' && value?.questionSchema?.value && value?.isVisibleToUser) {
                    return {
                        formFieldId: value.templateFormFieldId || '',
                        formFieldResponse: new Date(value.questionSchema.value)?.toString() || null,
                    };
                } else {
                    return {
                        formFieldId: value.templateFormFieldId || '',
                        formFieldResponse: value.questionSchema.value !== '' ? value.questionSchema.value?.toString() : null,
                    };
                }
            });
        payload.templateFormFields = [...templateFormFields, ...userFields];

        // Call API to submit form data
        setLoading(true);
        try {
            await clientSideFormSubmit(payload);
            setLoading(false);
            setIsSubmitted();
        } catch (error: any) {
            console.error(error);
            setLoading(false);
            setError(error?.response?.data || 'Something went wrong!');
            // Submit already had the form loaded — always allow dismissing via Retry.
            setCanRetryError(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchQuoteTemplate = async () => {
        if (quoteTemplateId) {
            try {
                await fetchQuoteFormData(quoteTemplateId);
                setCanRetryError(true);
            } catch (error) {
                console.error(error);
                setCanRetryError(isRetryableClientLoadError(error));
            }
        }
    };

    const handleRetry = () => {
        setError(null);
        setCanRetryError(true);
        // Load never succeeded — refetch. Submit failures just clear and return to the form.
        if (quoteTemplateId && clientViewData.quoteForm.templateFormFields.length === 0) {
            void fetchQuoteTemplate();
        }
    };

    useEffect(() => {
        fetchQuoteTemplate();
    }, [quoteTemplateId, lawFirmId]);

    useEffect(() => {
        if (error && !error.includes('expired')) {
            errorSnackbar(error, true);
        }
    }, [error]);

    const purchaserFieldNames = ['firstName', 'middleName', 'lastName', 'email', 'primaryPhone'];
    const purchaserFieldTemplates = clientViewData.quoteForm.templateFormFields.filter((f) => purchaserFieldNames.includes(f.name) && f.isVisibleToUser === true);

    // this is for the accordion change state
    const handleChange = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpand(newExpanded ? panel : false);
    };

    if (error) {
        const showRetry = canRetryError || clientViewData.quoteForm.templateFormFields.length > 0;
        return <ClientErrorPage message={typeof error === 'string' ? error : undefined} onRetry={showRetry ? handleRetry : undefined} isRetrying={loading} />;
    }

    return (
        <ClientViewWrapper loading={loading}>
            <Box sx={mainWrapperStyles}>
                <FormContainer>
                    <LawFirmLogo lawFirmId={lawFirmId} />
                    <Box>
                        <Typography variant={isMobile ? 'h3' : 'h1'}>{clientViewData.quoteForm.isSubmitted ? 'Request has been submitted.' : 'Request a Quote'}</Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.primary',
                                marginTop: isMobile ? '6px' : '16px',
                                fontSize: isMobile ? '12px' : '14px',
                                lineHeight: isMobile ? '16px' : '20px',
                                wordWrap: 'break-word',
                                overflowWrap: 'anywhere',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: convertToHtml(formDescriptionText),
                            }}
                        ></Typography>
                    </Box>
                </FormContainer>
                {!clientViewData.quoteForm.isSubmitted && (
                    <Box
                        sx={{
                            boxSizing: 'border-box',
                            backgroundColor: theme.palette.neutral.white,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            flexDirection: 'column',
                            gap: '0px',
                            width: '100%',
                            padding: isMobile ? '16px 8px' : '32px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            {clientViewData.quoteForm.templateFormFields?.length > 0 &&
                                clientViewData.quoteForm.templateFormFields
                                    ?.filter(
                                        (field) =>
                                            field.isVisibleToUser === true &&
                                            field.name !== 'firstName' &&
                                            field.name !== 'middleName' &&
                                            field.name !== 'lastName' &&
                                            field.name !== 'email' &&
                                            field.name !== 'primaryPhone',
                                    )
                                    ?.sort((a, b) => a?.displayOrder - b?.displayOrder)
                                    ?.map((field, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                width: '100%',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <FormComponent
                                                fieldObj={field}
                                                lawFirmId={lawFirmId as string}
                                                isError={field?.questionSchema?.isError}
                                                helper={field?.questionSchema?.helper}
                                                tabindex={field.displayOrder}
                                                disabled={loading}
                                                onFieldValueChange={(name: string, value: any) => {
                                                    handleValueChange(name, value, field);
                                                }}
                                                formGroupIndex={0}
                                            />
                                            {field.name === 'purchasersListedOnPurchaseAndSaleAgreement' &&
                                                Array.from({ length: numPurchasers }).map((_, idx) => (
                                                    <Accordion
                                                        key={`accordion-${idx}`}
                                                        expanded={expand === `${idx}`}
                                                        onChange={handleChange(`${idx}`)}
                                                        sx={{
                                                            marginX: '24px !important',
                                                            marginY: '12px !important',
                                                        }}
                                                    >
                                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                            <Typography variant="subtitle1">{`${ordinalNumbers[idx]} ${subtitle} info`}</Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Grid container spacing={2}>
                                                                {purchaserFieldTemplates.map((templateField) => {
                                                                    const fieldKey = templateField.name;
                                                                    return (
                                                                        <Grid item xs={12} sm={12} key={`${fieldKey}-${idx}`}>
                                                                            <FormComponent
                                                                                fieldObj={templateField}
                                                                                lawFirmId={lawFirmId as string}
                                                                                isError={purchaserFields?.[idx]?.[templateField.name]?.isError}
                                                                                helper={purchaserFields?.[idx]?.[templateField.name]?.helper || ''}
                                                                                tabindex={templateField.displayOrder}
                                                                                value={purchaserFields?.[idx]?.[templateField.name]?.formFieldResponse || ''}
                                                                                disabled={loading}
                                                                                onFieldValueChange={(name: string, value: any) => {
                                                                                    handlePurchaserFieldChange(name, value, templateField, idx);
                                                                                }}
                                                                                formGroupIndex={idx}
                                                                            />
                                                                            {purchaserFields?.[idx]?.[templateField.name]?.isError && hasSubmitted && (
                                                                                <div
                                                                                    style={{
                                                                                        color: 'rgb(150,44,39',
                                                                                        fontSize: '12px',
                                                                                        marginTop: '0px',
                                                                                        marginLeft: '25px',
                                                                                    }}
                                                                                >
                                                                                    {purchaserFields?.[idx]?.[templateField.name]?.helper}
                                                                                </div>
                                                                            )}
                                                                        </Grid>
                                                                    );
                                                                })}
                                                            </Grid>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                ))}
                                        </div>
                                    ))}
                        </Box>
                        {clientViewData.quoteForm.templateFormFields?.length > 0 && (
                            <ButtonSection
                                currentStep={0}
                                numSteps={1}
                                formSubmissionId={null}
                                isDraftSaveEnabled={false}
                                isDraftSubmitDisabled={false}
                                isNotAllRetainersSigned={false}
                                isSubmitDisabled={loading}
                                handleDraftSubmit={() => {}}
                                handleBackClick={() => {}}
                                handleNextClick={handleSubmit}
                                handleSubmitClick={handleSubmit}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </ClientViewWrapper>
    );
}
